import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./style.css";

const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition;

const TEXT = {
  ar: {
    tab: "صوت إلى نص",
    title: "حوّل صوتك إلى نص",
    subtitle: "اضغط على الميكروفون وابدأ بالتحدث",
    listening: "جارٍ الاستماع...",
    placeholder: "سيظهر كلامك هنا",
    clear: "مسح",
    copy: "نسخ",
    copied: "تم النسخ",
    unsupported: "متصفحك لا يدعم التعرف على الصوت. جرّب Chrome أو Edge.",
    denied: "يرجى السماح بالوصول إلى الميكروفون.",
  },
  en: {
    tab: "Voice to Text",
    title: "Turn your voice into text",
    subtitle: "Tap the mic and start speaking",
    listening: "Listening...",
    placeholder: "Your words will appear here",
    clear: "Clear",
    copy: "Copy",
    copied: "Copied",
    unsupported: "Your browser doesn't support speech recognition. Try Chrome or Edge.",
    denied: "Please allow microphone access.",
  },
};

const LOCALES = { ar: "ar-SA", en: "en-US" };

export default function VoiceDemo() {
  const [lang, setLang] = useState("ar");
  const [listening, setListening] = useState(false);
  const [finalText, setFinalText] = useState("");
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(SpeechRecognition ? "" : "unsupported");
  const [copied, setCopied] = useState(false);
  const recognitionRef = useRef(null);
  const t = TEXT[lang];

  useEffect(() => () => recognitionRef.current && recognitionRef.current.abort(), []);

  const start = () => {
    if (!SpeechRecognition) return;
    const rec = new SpeechRecognition();
    rec.lang = LOCALES[lang];
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (e) => {
      let interimText = "";
      let added = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const chunk = e.results[i][0].transcript;
        if (e.results[i].isFinal) added += chunk + " ";
        else interimText += chunk;
      }
      if (added) setFinalText((prev) => prev + added);
      setInterim(interimText);
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") setError("denied");
    };
    rec.onend = () => {
      setListening(false);
      setInterim("");
    };

    setError("");
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  const stop = () => recognitionRef.current && recognitionRef.current.stop();

  const switchLang = (next) => {
    if (next === lang) return;
    if (listening) recognitionRef.current.abort();
    setLang(next);
  };

  const copy = () => {
    navigator.clipboard.writeText(finalText.trim()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const hasText = finalText || interim;

  return (
    <div className="vd-page" dir={lang === "ar" ? "rtl" : "ltr"} lang={lang}>
      <Helmet>
        <title>{t.tab}</title>
        <link rel="icon" type="image/svg+xml" href="/mic-icon.svg" />
        <link rel="apple-touch-icon" href="/mic-icon.svg" />
      </Helmet>
      <div className="vd-card">
        <div className="vd-toggle" dir="ltr">
          <span className={`vd-toggle-pill ${lang}`} />
          <button className={lang === "en" ? "active" : ""} onClick={() => switchLang("en")}>
            English
          </button>
          <button className={lang === "ar" ? "active" : ""} onClick={() => switchLang("ar")}>
            العربية
          </button>
        </div>

        <h1 className="vd-title">{t.title}</h1>
        <p className="vd-subtitle">{listening ? t.listening : t.subtitle}</p>

        <button
          className={`vd-mic ${listening ? "on" : ""}`}
          onClick={listening ? stop : start}
          disabled={!SpeechRecognition}
          aria-label="microphone"
        >
          <span className="vd-ring" />
          <span className="vd-ring delay" />
          <i className={`fa-solid ${listening ? "fa-stop" : "fa-microphone"}`} />
        </button>

        {error && <p className="vd-error">{t[error]}</p>}

        <div className={`vd-output ${hasText ? "" : "empty"}`}>
          {hasText ? (
            <>
              {finalText}
              <span className="vd-interim">{interim}</span>
            </>
          ) : (
            t.placeholder
          )}
        </div>

        <div className="vd-actions">
          <button onClick={copy} disabled={!finalText}>
            <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"}`} /> {copied ? t.copied : t.copy}
          </button>
          <button onClick={() => { setFinalText(""); setInterim(""); }} disabled={!hasText}>
            <i className="fa-solid fa-eraser" /> {t.clear}
          </button>
        </div>
      </div>
    </div>
  );
}
